import { useCallback, useEffect, useState } from 'react';
import { Link } from 'react-router-dom';
import { apiFetch } from '@/lib/api';
import { useSession } from '@/hooks/useSession';
import { PageHeader } from '@/components/ui/PageHeader';
import { Card } from '@/components/ui/Card';
import { Button } from '@/components/ui/Button';
import { Spinner } from '@/components/ui/Spinner';
import { Modal } from '@/components/ui/Modal';
import { inputClass, labelClass, inlineErrorClass, inlineSuccessClass } from '@/components/ui/inputStyles';

type WechatAcc = {
  id: number;
  authorizer_appid: string;
  authorizer_nick_name: string;
  push_enabled: boolean;
};

type MpType = { key: string; label: string };

type EntrySiteOption = { id: number; site_name: string };

/** 与 api.php 中 wes_oa_mass_push_expand_entry_separator 一致 */
const ENTRY_SEPARATOR_VARS_HINT =
  '可用占位符：{title} {site_name} {phone} {type_key} {type_label} {info_no} {registered_date} ' +
  '{body} {body_short} {body_br} {body_short_br} {thumb_url} {thumb_img}（含 HTML 片段时 thumb 等为嵌入图；无 {thumb_*} 的纯文本模板不会插入图）';

type OaMassPushConfig = {
  enabled: boolean;
  wechat_account_id: number;
  push_time_local: string;
  content_style: 'show_phone' | 'miniprogram_link';
  /** plain_separator：无 HTML 版式、保留换行、条间用 entry_separator；rich_html：原 HTML 分块 */
  body_format: 'plain_separator' | 'rich_html';
  /** 条与条之间的分隔行（仅 plain_separator）；无 {变量} 时变量指上一条；含 {变量} 时每条仅按模板用当前条展开 */
  entry_separator: string;
  /** 分隔行内 {thumb_img} 等图的显示宽度（像素），服务端等比缩放已落盘图 */
  separator_image_width_px: number;
  order_mode: 'time_desc' | 'group_by_site';
  digest_miniprogram_id: number;
  catalog_miniprogram_id: number;
  days: number;
  max_items: number;
  /** 空字符串表示「未分类」条目 */
  type_keys: string[];
  /** 空数组=该小程序全部展示站点；非空=仅从所列站点 id 取数 */
  catalog_site_ids: number[];
};

type MpRow = { id: number; name: string; enabled: boolean };

const DEFAULT_ENTRY_SEP = '——————————';

const defaultConfig = (): OaMassPushConfig => ({
  enabled: false,
  wechat_account_id: 0,
  push_time_local: '09:00',
  content_style: 'show_phone',
  body_format: 'plain_separator',
  entry_separator: DEFAULT_ENTRY_SEP,
  separator_image_width_px: 600,
  order_mode: 'time_desc',
  digest_miniprogram_id: 0,
  catalog_miniprogram_id: 0,
  days: 7,
  max_items: 30,
  type_keys: [],
  catalog_site_ids: [],
});

type OaMassPushRule = OaMassPushConfig & {
  id: string;
  name: string;
  last_run_ymd?: string;
};

const defaultRule = (): OaMassPushRule => ({
  id: '',
  name: '新推送规则',
  last_run_ymd: '',
  ...defaultConfig(),
});

function mergeRuleFromApi(raw: unknown): OaMassPushRule {
  const c = raw && typeof raw === 'object' ? (raw as Record<string, unknown>) : {};
  const base = defaultRule();
  const cfgIn = c as Partial<OaMassPushConfig>;
  const mergedCfg: OaMassPushConfig = {
    ...defaultConfig(),
    ...cfgIn,
    digest_miniprogram_id: cfgIn.digest_miniprogram_id ?? 0,
    catalog_miniprogram_id: cfgIn.catalog_miniprogram_id ?? 0,
    days: typeof cfgIn.days === 'number' ? cfgIn.days : 7,
    max_items: typeof cfgIn.max_items === 'number' ? cfgIn.max_items : 30,
    type_keys: Array.isArray(cfgIn.type_keys)
      ? cfgIn.type_keys.map((x) => (typeof x === 'string' ? x : String(x)))
      : [],
    catalog_site_ids: Array.isArray(cfgIn.catalog_site_ids)
      ? cfgIn.catalog_site_ids
          .map((x) => (typeof x === 'number' && !Number.isNaN(x) ? Math.round(x) : parseInt(String(x), 10)))
          .filter((n) => n > 0)
      : [],
    body_format: cfgIn.body_format === 'rich_html' ? 'rich_html' : 'plain_separator',
    entry_separator:
      typeof cfgIn.entry_separator === 'string' && cfgIn.entry_separator.trim() !== ''
        ? cfgIn.entry_separator
        : DEFAULT_ENTRY_SEP,
    separator_image_width_px:
      typeof cfgIn.separator_image_width_px === 'number' && !Number.isNaN(cfgIn.separator_image_width_px)
        ? Math.min(900, Math.max(64, Math.round(cfgIn.separator_image_width_px)))
        : 600,
  };
  return {
    ...mergedCfg,
    id: typeof c.id === 'string' ? c.id : '',
    name: typeof c.name === 'string' && c.name.trim() !== '' ? String(c.name).trim() : base.name,
    last_run_ymd: typeof c.last_run_ymd === 'string' ? c.last_run_ymd : '',
  };
}

const UNCAT_KEY = '__uncat__';

function typeKeyForApi(uiKey: string): string {
  return uiKey === UNCAT_KEY ? '' : uiKey;
}

export function WechatOfficialPushPage() {
  const { currentSiteId, currentSiteName } = useSession();
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [err, setErr] = useState('');
  const [msg, setMsg] = useState('');
  const [accounts, setAccounts] = useState<WechatAcc[]>([]);
  const [rules, setRules] = useState<OaMassPushRule[]>([]);
  const [lastRunYmd, setLastRunYmd] = useState('');
  const [cronHint, setCronHint] = useState('');
  const [miniprograms, setMiniprograms] = useState<MpRow[]>([]);
  const [catalogTypes, setCatalogTypes] = useState<MpType[]>([]);
  const [catalogEntrySites, setCatalogEntrySites] = useState<EntrySiteOption[]>([]);
  const [typesLoading, setTypesLoading] = useState(false);
  const [editorModalOpen, setEditorModalOpen] = useState(false);
  const [ruleDraft, setRuleDraft] = useState<OaMassPushRule>(defaultRule);
  const [previewBusyRuleId, setPreviewBusyRuleId] = useState<string | null>(null);
  const [runBusyRuleId, setRunBusyRuleId] = useState<string | null>(null);
  const [previewModalOpen, setPreviewModalOpen] = useState(false);
  const [previewHead, setPreviewHead] = useState('');
  const [previewClipboard, setPreviewClipboard] = useState('');
  const [previewBodyFormat, setPreviewBodyFormat] = useState<'plain_separator' | 'rich_html'>('plain_separator');
  const [previewActiveRuleId, setPreviewActiveRuleId] = useState('');
  const [copyTip, setCopyTip] = useState('');

  const loadCatalogTypes = useCallback(async (mpId: number) => {
    if (mpId < 1) {
      setCatalogTypes([]);
      setCatalogEntrySites([]);
      return;
    }
    setTypesLoading(true);
    try {
      const r = await apiFetch<{
        ok: boolean;
        miniprogram?: { types?: MpType[]; entry_site_options?: unknown[] };
      }>('get_miniprogram', {
        method: 'GET',
        query: { id: mpId },
      });
      if (r.ok && r.miniprogram?.types) {
        setCatalogTypes(r.miniprogram.types);
      } else {
        setCatalogTypes([]);
      }
      const rawOpts = r.ok && r.miniprogram && Array.isArray(r.miniprogram.entry_site_options)
        ? r.miniprogram.entry_site_options
        : [];
      const sites: EntrySiteOption[] = [];
      for (const o of rawOpts) {
        if (o && typeof o === 'object') {
          const rec = o as Record<string, unknown>;
          const id = typeof rec.id === 'number' ? rec.id : parseInt(String(rec.id ?? 0), 10);
          if (id > 0) {
            sites.push({ id, site_name: typeof rec.site_name === 'string' ? rec.site_name : String(rec.site_name ?? '') });
          }
        }
      }
      sites.sort((a, b) => a.id - b.id);
      setCatalogEntrySites(sites);
    } catch {
      setCatalogTypes([]);
      setCatalogEntrySites([]);
    } finally {
      setTypesLoading(false);
    }
  }, []);

  const load = useCallback(async () => {
    if (currentSiteId < 1) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setErr('');
    setMsg('');
    try {
      const [gc, mp] = await Promise.all([
        apiFetch<{
          ok: boolean;
          rules?: unknown[];
          wechat_accounts?: WechatAcc[];
          last_run_ymd?: string;
          cron_oa_mass_push_url_hint?: string;
        }>('get_oa_mass_push_config', {
          method: 'GET',
          query: { site_id: currentSiteId },
        }),
        apiFetch<{ ok: boolean; items: MpRow[] }>('list_miniprograms', {
          method: 'GET',
          query: { page: 1, per_page: 200 },
        }),
      ]);
      if (!gc.ok) {
        setErr('无法加载公众号推送配置');
        return;
      }
      const list = Array.isArray(gc.rules) ? gc.rules.map((x) => mergeRuleFromApi(x)) : [];
      setRules(list);
      setAccounts(gc.wechat_accounts ?? []);
      setLastRunYmd((gc.last_run_ymd ?? '').trim());
      setCronHint((gc.cron_oa_mass_push_url_hint ?? '').trim());
      if (mp.ok) {
        setMiniprograms(mp.items ?? []);
      }
      const firstMp = list[0]?.catalog_miniprogram_id ?? 0;
      await loadCatalogTypes(firstMp);
    } catch (e) {
      if (e instanceof Error && e.message === 'SESSION_EXPIRED') {
        return;
      }
      setErr(e instanceof Error ? e.message : '加载失败');
    } finally {
      setLoading(false);
    }
  }, [currentSiteId, loadCatalogTypes]);

  useEffect(() => {
    void load();
  }, [load]);

  function toggleCatalogSiteId(siteId: number) {
    setRuleDraft((c) => {
      const set = new Set(c.catalog_site_ids);
      if (set.has(siteId)) {
        set.delete(siteId);
      } else {
        set.add(siteId);
      }
      return { ...c, catalog_site_ids: Array.from(set).sort((a, b) => a - b) };
    });
  }

  function toggleRuleTypeKey(uiKey: string) {
    const apiKey = typeKeyForApi(uiKey);
    setRuleDraft((c) => {
      const set = new Set(c.type_keys);
      if (set.has(apiKey)) {
        set.delete(apiKey);
      } else {
        set.add(apiKey);
      }
      return { ...c, type_keys: [...set] };
    });
  }

  function openCreateRule() {
    setErr('');
    setRuleDraft(defaultRule());
    setEditorModalOpen(true);
    void loadCatalogTypes(0);
  }

  function openEditRule(rule: OaMassPushRule) {
    setErr('');
    setRuleDraft({ ...rule });
    setEditorModalOpen(true);
    void loadCatalogTypes(rule.catalog_miniprogram_id);
  }

  async function saveRuleFromEditor() {
    if (currentSiteId < 1) return;
    setSaving(true);
    setErr('');
    setMsg('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string; rule?: OaMassPushRule }>('save_oa_mass_push_rule', {
        method: 'POST',
        body: JSON.stringify({ site_id: currentSiteId, rule: ruleDraft }),
      });
      if (!r.ok) {
        setErr(r.err || '保存失败');
        return;
      }
      setMsg('已保存规则');
      setEditorModalOpen(false);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : '保存失败');
    } finally {
      setSaving(false);
    }
  }

  async function deleteRuleFromEditor() {
    if (currentSiteId < 1 || !ruleDraft.id) return;
    if (!window.confirm('确定删除此推送规则？不可恢复。')) return;
    setSaving(true);
    setErr('');
    try {
      const r = await apiFetch<{ ok: boolean; err?: string }>('delete_oa_mass_push_rule', {
        method: 'POST',
        body: JSON.stringify({ site_id: currentSiteId, rule_id: ruleDraft.id }),
      });
      if (!r.ok) {
        setErr(r.err || '删除失败');
        return;
      }
      setMsg('已删除规则');
      setEditorModalOpen(false);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : '删除失败');
    } finally {
      setSaving(false);
    }
  }

  async function runPreview(ruleId: string) {
    if (currentSiteId < 1 || !ruleId) return;
    setPreviewBusyRuleId(ruleId);
    setPreviewModalOpen(false);
    setCopyTip('');
    setErr('');
    setPreviewActiveRuleId(ruleId);
    try {
      const r = await apiFetch<{
        ok: boolean;
        err?: string;
        title?: string;
        digest?: string;
        html?: string;
        clipboard_text?: string;
        body_format?: string;
        entry_count?: number;
        skipped?: boolean;
        detail?: string;
      }>('preview_oa_mass_push', {
        method: 'GET',
        query: { site_id: currentSiteId, rule_id: ruleId },
      });
      if (!r.ok) {
        setErr(r.err || '预览失败');
        return;
      }
      if (r.skipped) {
        setErr('');
        const d = r.detail || '';
        setMsg(
          d === 'no_push_rules'
            ? '暂无推送规则，请先点击「创建推送规则」并保存。'
            : `预览跳过：${d || '无条目'}`,
        );
        return;
      }
      const head = `草稿标题：${r.title || '—'}\n摘要：${r.digest || '—'}\n条数：${r.entry_count ?? 0}`;
      setPreviewHead(head);
      setPreviewClipboard((r.clipboard_text ?? '').trim() !== '' ? r.clipboard_text! : r.html ?? '');
      setPreviewBodyFormat(r.body_format === 'rich_html' ? 'rich_html' : 'plain_separator');
      setPreviewModalOpen(true);
    } catch (e) {
      setErr(e instanceof Error ? e.message : '预览失败');
    } finally {
      setPreviewBusyRuleId(null);
    }
  }

  async function copyPreviewToClipboard() {
    const t = previewClipboard;
    if (!t) {
      setCopyTip('无可复制内容');
      return;
    }
    let text = t;
    let wechatImgRewritten = 0;
    if (
      previewBodyFormat === 'plain_separator' &&
      currentSiteId >= 1 &&
      /<\s*img\b/i.test(t) &&
      !/mmbiz\.qpic\.cn/i.test(t)
    ) {
      setCopyTip('正在上传图片到公众号素材…');
      try {
        const r = await apiFetch<{
          ok: boolean;
          err?: string;
          clipboard_text?: string;
          images_rewritten?: number;
        }>('oa_mass_push_clipboard_for_wechat', {
          method: 'POST',
          body: JSON.stringify({ site_id: currentSiteId, rule_id: previewActiveRuleId }),
        });
        if (!r.ok) {
          setCopyTip(r.err || '处理失败');
          window.setTimeout(() => setCopyTip(''), 4200);
          return;
        }
        wechatImgRewritten = typeof r.images_rewritten === 'number' ? r.images_rewritten : 0;
        text = (r.clipboard_text ?? t).trim() || t;
      } catch (e) {
        setCopyTip(e instanceof Error ? e.message : '请求失败');
        window.setTimeout(() => setCopyTip(''), 4200);
        return;
      }
    }
    const looksLikeHtml = /<\s*(?:p|img|div|section|br)\b/i.test(text);
    if (looksLikeHtml && typeof ClipboardItem !== 'undefined' && navigator.clipboard?.write) {
      try {
        const docHtml = `<!DOCTYPE html><html><head><meta charset="utf-8"></head><body>${text}</body></html>`;
        const htmlBlob = new Blob([docHtml], { type: 'text/html' });
        let plain = text.replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
        try {
          const doc = new DOMParser().parseFromString(`<div>${text}</div>`, 'text/html');
          const tx = doc.body.textContent?.replace(/\n{3,}/g, '\n\n').trim();
          if (tx) {
            plain = tx;
          }
        } catch {
          /* keep regex fallback */
        }
        const plainBlob = new Blob([plain], { type: 'text/plain' });
        await navigator.clipboard.write([new ClipboardItem({ 'text/html': htmlBlob, 'text/plain': plainBlob })]);
        setCopyTip(
          wechatImgRewritten > 0
            ? `已复制（已将 ${wechatImgRewritten} 张图上传为公众号素材并替换链接，可粘贴到公众号正文）`
            : /mmbiz\.qpic\.cn/i.test(text)
              ? '已复制（正文图为微信素材地址，可粘贴到公众号）'
              : '已复制（含 HTML，可粘贴到公众号正文）',
        );
        window.setTimeout(() => setCopyTip(''), 3200);
        return;
      } catch {
        /* fall through to writeText */
      }
    }
    try {
      await navigator.clipboard.writeText(text);
      setCopyTip('已复制到剪贴板');
    } catch {
      setCopyTip('复制失败，请手动全选下方文本复制');
    }
    window.setTimeout(() => setCopyTip(''), 2800);
  }

  async function runPushNow(ruleId: string) {
    if (currentSiteId < 1 || !ruleId) return;
    if (!window.confirm('将向所选公众号写入一条「汇总」图文草稿（需有本地图片作封面）。确定继续？')) {
      return;
    }
    setRunBusyRuleId(ruleId);
    setErr('');
    setMsg('');
    try {
      const r = await apiFetch<{
        ok: boolean;
        err?: string;
        detail?: string;
        media_id?: string;
        title?: string;
        entry_count?: number;
        skipped?: boolean;
      }>('run_oa_mass_push_now', {
        method: 'POST',
        body: JSON.stringify({ site_id: currentSiteId, ignore_enabled: true, rule_id: ruleId }),
      });
      if (r.skipped) {
        setErr('未执行：' + (r.detail || '未知原因'));
        return;
      }
      if (!r.ok) {
        setErr(r.err || r.detail || '执行失败');
        return;
      }
      setMsg(`已写入公众号草稿箱。media_id=${r.media_id || '—'}，标题：${r.title || '—'}，共 ${r.entry_count ?? 0} 条信息。`);
      await load();
    } catch (e) {
      setErr(e instanceof Error ? e.message : '执行失败');
    } finally {
      setRunBusyRuleId(null);
    }
  }

  function accountNick(accId: number): string {
    const a = accounts.find((x) => x.id === accId);
    if (!a) {
      return accId > 0 ? `账号 #${accId}` : '（未选择公众号）';
    }
    return (a.authorizer_nick_name || a.authorizer_appid || `账号 #${accId}`) + (a.push_enabled ? '' : ' · 推送已关');
  }

  function mpTitle(mpId: number): string {
    const m = miniprograms.find((x) => x.id === mpId);
    if (!m) {
      return mpId > 0 ? `小程序 #${mpId}` : '（未选择小程序）';
    }
    return `#${m.id} ${m.name}${!m.enabled ? '（已停用）' : ''}`;
  }

  if (currentSiteId < 1) {
    return <p className="text-sm text-amber-800">请先在上方「当前工作站点」中选择站点后再配置公众号推送。</p>;
  }

  return (
    <div className="space-y-6">
      <PageHeader
        title="公众号推送"
        description="可创建多条推送规则；每条规则独立配置公众号、取数小程序、时刻与正文样式。计划任务会按规则分别判断「启用 / 时刻窗口 / 当日是否已跑过」后写入草稿。"
        actions={
          <div className="flex flex-wrap items-center justify-end gap-2">
            <Button type="button" variant="secondary" onClick={() => void load()} disabled={loading}>
              重新加载
            </Button>
            <Link to="/interfaces">
              <Button type="button" variant="secondary">
                返回接入与推送
              </Button>
            </Link>
            <Button type="button" variant="primary" onClick={() => openCreateRule()} disabled={loading}>
              创建推送规则
            </Button>
          </div>
        }
      />

      {loading ? (
        <Spinner label="加载配置" />
      ) : (
        <>
          {err ? <p className={inlineErrorClass}>{err}</p> : null}
          {msg ? <p className={inlineSuccessClass}>{msg}</p> : null}

          <Card title="说明">
            <p className="text-sm leading-relaxed text-slate-600">
              保存规则后，可由<strong>计划任务</strong>定期请求接口，在配置的时刻窗口内自动生成<strong>一条汇总图文草稿</strong>写入所选公众号草稿箱（与普通单条草稿相同，运营在公众平台选稿发布）。同一自然日成功执行一次后会跳过，避免重复草稿。
            </p>
            <p className="mt-2 text-sm leading-relaxed text-slate-600">
              「推送目标公众号」须为当前站点已绑定账号。汇总稿为<strong>普通图文</strong>，需至少一条信息带有<strong>已落盘本地图</strong>作封面。
            </p>
            {cronHint ? (
              <p className="mt-3 text-xs leading-relaxed text-slate-500">
                定时任务 URL 示例（请将 key 换为服务器 <code className="font-mono">config.php</code> 中的 cron_key）：
                <span className="mt-1 block break-all font-mono text-[11px] text-slate-700">{cronHint}</span>
              </p>
            ) : null}
            {lastRunYmd ? (
              <p className="mt-2 text-xs text-slate-600">
                上次成功生成摘要草稿日期：<span className="font-mono">{lastRunYmd}</span>
              </p>
            ) : null}
          </Card>

          {rules.length === 0 ? (
            <Card title="推送规则">
              <p className="text-sm text-slate-600">
                暂无规则。点击右上角「创建推送规则」添加；每条规则保存后可在卡片上使用「预览汇总」「生成草稿」。
              </p>
            </Card>
          ) : (
            <div className="grid gap-4 md:grid-cols-2 xl:grid-cols-3">
              {rules.map((rule) => (
                <div
                  key={rule.id || rule.name}
                  className="flex flex-col rounded-xl border border-slate-200/90 bg-white p-4 shadow-sm"
                >
                  <div className="flex items-start justify-between gap-2">
                    <div className="min-w-0 flex-1">
                      <h3 className="truncate text-base font-semibold text-slate-900">{rule.name || '未命名规则'}</h3>
                      <p className="mt-1 text-xs text-slate-500">
                        {rule.enabled ? <span className="text-emerald-700">已启用</span> : <span>未启用</span>}
                        {' · '}
                        {rule.push_time_local}
                        {' · '}
                        {accountNick(rule.wechat_account_id)}
                      </p>
                      <p className="mt-2 flex flex-wrap gap-x-3 gap-y-1 text-xs text-slate-600">
                        <span>
                          站点：
                          <strong className="font-medium text-slate-800">
                            {currentSiteName?.trim() ? currentSiteName : `站点 #${currentSiteId}`}
                          </strong>
                        </span>
                        <span>
                          取数：
                          <strong className="font-medium text-slate-800">{mpTitle(rule.catalog_miniprogram_id)}</strong>
                        </span>
                        <span>
                          几天内：
                          <strong className="font-medium text-slate-800">{rule.days} 天</strong>
                        </span>
                      </p>
                      {rule.last_run_ymd ? (
                        <p className="mt-1 text-xs text-slate-500">
                          本规则上次成功生成草稿：<span className="font-mono">{rule.last_run_ymd}</span>
                        </p>
                      ) : null}
                    </div>
                    <button
                      type="button"
                      className="shrink-0 text-xs font-medium text-indigo-600 hover:text-indigo-800"
                      onClick={() => openEditRule(rule)}
                    >
                      编辑
                    </button>
                  </div>
                  <div className="mt-4 flex flex-wrap gap-2">
                    <Button
                      type="button"
                      variant="secondary"
                      disabled={!rule.id || previewBusyRuleId === rule.id}
                      onClick={() => void runPreview(rule.id)}
                    >
                      {previewBusyRuleId === rule.id ? '预览中…' : '预览汇总'}
                    </Button>
                    <Button
                      type="button"
                      variant="primary"
                      disabled={!rule.id || runBusyRuleId === rule.id}
                      onClick={() => void runPushNow(rule.id)}
                    >
                      {runBusyRuleId === rule.id ? '写入中…' : '生成草稿'}
                    </Button>
                  </div>
                </div>
              ))}
            </div>
          )}

          <Modal
            open={editorModalOpen}
            title={ruleDraft.id ? '编辑推送规则' : '创建推送规则'}
            wide="xl"
            onClose={() => {
              if (!saving) setEditorModalOpen(false);
            }}
            footer={
              <div className="flex w-full flex-wrap items-center gap-2">
                {ruleDraft.id ? (
                  <Button
                    type="button"
                    variant="secondary"
                    className="border-red-200 text-red-700 hover:bg-red-50"
                    onClick={() => void deleteRuleFromEditor()}
                    disabled={saving}
                  >
                    删除此规则
                  </Button>
                ) : null}
                <span className="flex-1" />
                <Button type="button" variant="secondary" onClick={() => setEditorModalOpen(false)} disabled={saving}>
                  取消
                </Button>
                <Button type="button" variant="primary" disabled={saving} onClick={() => void saveRuleFromEditor()}>
                  {saving ? '保存中…' : '保存规则'}
                </Button>
              </div>
            }
          >
            <div className="max-h-[min(72vh,640px)] space-y-4 overflow-y-auto pr-1">
              <label className={labelClass}>
                <span>规则名称</span>
                <input
                  type="text"
                  className={inputClass}
                  maxLength={80}
                  value={ruleDraft.name}
                  onChange={(e) => setRuleDraft((c) => ({ ...c, name: e.target.value.slice(0, 80) }))}
                  placeholder="例如：每日二手汇总"
                />
              </label>

              <Card title="全局选项">
                <div className="grid gap-4 md:grid-cols-2">
                  <label className={`${labelClass} flex items-center gap-2`}>
                    <input
                      type="checkbox"
                      checked={ruleDraft.enabled}
                      onChange={(e) => setRuleDraft((c) => ({ ...c, enabled: e.target.checked }))}
                    />
                    <span>启用本规则（计划任务识别）</span>
                  </label>
                  <label className={labelClass}>
                    <span>推送目标公众号</span>
                    <select
                      className={inputClass}
                      value={ruleDraft.wechat_account_id || ''}
                      onChange={(e) =>
                        setRuleDraft((c) => ({ ...c, wechat_account_id: Number(e.target.value) || 0 }))
                      }
                    >
                      <option value="">（未选择）</option>
                      {accounts.map((a) => (
                        <option key={a.id} value={a.id}>
                          {(a.authorizer_nick_name || a.authorizer_appid || '未命名') +
                            (a.push_enabled ? '' : ' · 推送已关')}
                        </option>
                      ))}
                    </select>
                  </label>
                  <label className={labelClass}>
                    <span>每日推送时刻（服务器本地时区 HH:mm）</span>
                    <input
                      type="time"
                      className={inputClass}
                      value={ruleDraft.push_time_local.length === 5 ? ruleDraft.push_time_local : '09:00'}
                      onChange={(e) =>
                        setRuleDraft((c) => ({ ...c, push_time_local: e.target.value.slice(0, 5) }))
                      }
                    />
                  </label>
                  <label className={labelClass}>
                    <span>信息样式</span>
                    <select
                      className={inputClass}
                      value={ruleDraft.content_style}
                      onChange={(e) =>
                        setRuleDraft((c) => ({
                          ...c,
                          content_style: e.target.value === 'miniprogram_link' ? 'miniprogram_link' : 'show_phone',
                        }))
                      }
                    >
                      <option value="show_phone">正文中直接展示电话等文本</option>
                      <option value="miniprogram_link">优先使用小程序链接样式（与所选展示小程序一致）</option>
                    </select>
                  </label>
                  <label className={labelClass}>
                    <span>汇总正文格式</span>
                    <select
                      className={inputClass}
                      value={ruleDraft.body_format}
                      onChange={(e) =>
                        setRuleDraft((c) => ({
                          ...c,
                          body_format: e.target.value === 'rich_html' ? 'rich_html' : 'plain_separator',
                        }))
                      }
                    >
                      <option value="plain_separator">纯文本（保留换行，条与条之间用分隔行）</option>
                      <option value="rich_html">HTML 分块（标题/电话/小程序链等样式）</option>
                    </select>
                  </label>
                  {ruleDraft.body_format === 'plain_separator' ? (
                    <>
                      <label className={labelClass}>
                        <span>分隔行内图片显示宽度（像素）</span>
                        <input
                          type="number"
                          min={64}
                          max={900}
                          className={inputClass}
                          value={ruleDraft.separator_image_width_px}
                          onChange={(e) =>
                            setRuleDraft((c) => ({
                              ...c,
                              separator_image_width_px: Math.min(
                                900,
                                Math.max(64, parseInt(e.target.value, 10) || 600),
                              ),
                            }))
                          }
                        />
                      </label>
                      <label className={`${labelClass} md:col-span-2`}>
                        <span>条与条之间的分隔行（支持变量）</span>
                        <textarea
                          className={`${inputClass} min-h-[7rem] font-mono text-sm`}
                          value={ruleDraft.entry_separator}
                          onChange={(e) =>
                            setRuleDraft((c) => ({ ...c, entry_separator: e.target.value.slice(0, 4000) }))
                          }
                          placeholder={`${DEFAULT_ENTRY_SEP}\n或：{body_short}\n{thumb_img}`}
                          rows={5}
                        />
                        <span className="mt-1 block text-xs leading-relaxed text-slate-500">
                          模板中若出现 <code className="rounded bg-slate-100 px-1">{'{...}'}</code>，则每条分隔行按<strong>当前条</strong>展开；不含占位符时行为与原先相同（变量语义仍指「上一条 / 当前条」由服务端逻辑决定）。
                          <span className="mt-1 block font-mono text-[11px] text-slate-600">{ENTRY_SEPARATOR_VARS_HINT}</span>
                        </span>
                      </label>
                    </>
                  ) : null}
                  <label className={`${labelClass} md:col-span-2`}>
                    <span>排序方式</span>
                    <select
                      className={inputClass}
                      value={ruleDraft.order_mode}
                      onChange={(e) => {
                        const v = e.target.value;
                        setRuleDraft((c) => ({
                          ...c,
                          order_mode: v === 'group_by_site' ? 'group_by_site' : 'time_desc',
                        }));
                      }}
                    >
                      <option value="time_desc">与小程序目录相同排序（置顶/刷新规则一致）</option>
                      <option value="group_by_site">按站点分块，块内按创建时间倒序</option>
                    </select>
                  </label>
                </div>
              </Card>

              <Card title="取数范围（小程序展示目录）">
                <p className="mb-3 text-sm text-slate-600">
                  数据来自该小程序在「小程序管理」中已勾选站点下的<strong>展示中</strong>信息。
                </p>
                <div className="grid gap-4 md:grid-cols-2">
                  <label className={`${labelClass} md:col-span-2`}>
                    <span>展示用微信小程序</span>
                    <select
                      className={inputClass}
                      value={ruleDraft.catalog_miniprogram_id || ''}
                      onChange={(e) => {
                        const id = Number(e.target.value) || 0;
                        setRuleDraft((c) => ({
                          ...c,
                          catalog_miniprogram_id: id,
                          type_keys: [],
                          catalog_site_ids: [],
                        }));
                        void loadCatalogTypes(id);
                      }}
                    >
                      <option value="">（请选择）</option>
                      {miniprograms.map((m) => (
                        <option key={m.id} value={m.id}>
                          #{m.id} {m.name}
                          {!m.enabled ? '（已停用）' : ''}
                        </option>
                      ))}
                    </select>
                  </label>
                  {ruleDraft.catalog_miniprogram_id >= 1 ? (
                    <div className={`${labelClass} md:col-span-2`}>
                      <span>取数站点（多选）</span>
                      <p className="mb-2 text-xs leading-relaxed text-slate-500">
                        不勾选任何站点表示<strong>该小程序全部展示站点</strong>（含已展开子站）；勾选后仅从所选站点拉取信息。
                      </p>
                      {typesLoading ? (
                        <p className="text-sm text-slate-500">加载站点列表中…</p>
                      ) : catalogEntrySites.length === 0 ? (
                        <p className="text-sm text-slate-600">该小程序暂无可用展示站点，或当前账号无站点访问权限。</p>
                      ) : (
                        <div className="flex flex-wrap items-center gap-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3">
                          <button
                            type="button"
                            className="text-xs font-medium text-indigo-600 hover:text-indigo-800"
                            onClick={() =>
                              setRuleDraft((c) => ({
                                ...c,
                                catalog_site_ids: catalogEntrySites.map((s) => s.id),
                              }))
                            }
                          >
                            全选
                          </button>
                          <span className="text-slate-300">|</span>
                          <button
                            type="button"
                            className="text-xs font-medium text-slate-600 hover:text-slate-800"
                            onClick={() => setRuleDraft((c) => ({ ...c, catalog_site_ids: [] }))}
                          >
                            清除（全部站点）
                          </button>
                          <div className="mt-2 flex w-full flex-wrap gap-x-4 gap-y-2">
                            {catalogEntrySites.map((s) => (
                              <label key={s.id} className="flex cursor-pointer items-center gap-2 text-sm">
                                <input
                                  type="checkbox"
                                  checked={ruleDraft.catalog_site_ids.includes(s.id)}
                                  onChange={() => toggleCatalogSiteId(s.id)}
                                />
                                <span>
                                  <span className="font-mono text-xs">#{s.id}</span>
                                  {s.site_name ? <span className="text-slate-600"> · {s.site_name}</span> : null}
                                </span>
                              </label>
                            ))}
                          </div>
                        </div>
                      )}
                      {(() => {
                        const known = new Set(catalogEntrySites.map((x) => x.id));
                        const orphan = ruleDraft.catalog_site_ids.filter((id) => !known.has(id));
                        if (orphan.length === 0) {
                          return null;
                        }
                        return (
                          <p className="mt-2 text-xs text-amber-800">
                            规则中仍包含当前列表不可见的站点 id（可能已无权限或已从小程序移除）：{' '}
                            <span className="font-mono">{orphan.join(', ')}</span>。请重新勾选或保存前删除，否则可能保存失败。
                          </p>
                        );
                      })()}
                    </div>
                  ) : null}
                  <label className={labelClass}>
                    <span>创建时间在几天内</span>
                    <input
                      type="number"
                      min={1}
                      max={365}
                      className={inputClass}
                      value={ruleDraft.days}
                      onChange={(e) =>
                        setRuleDraft((c) => ({
                          ...c,
                          days: Math.min(365, Math.max(1, parseInt(e.target.value, 10) || 7)),
                        }))
                      }
                    />
                  </label>
                  <label className={labelClass}>
                    <span>最多取几条（目录内截断）</span>
                    <input
                      type="number"
                      min={1}
                      max={100}
                      className={inputClass}
                      value={ruleDraft.max_items}
                      onChange={(e) =>
                        setRuleDraft((c) => ({
                          ...c,
                          max_items: Math.min(100, Math.max(1, parseInt(e.target.value, 10) || 30)),
                        }))
                      }
                    />
                  </label>
                </div>

                <div className="mt-4 border-t border-slate-200 pt-4">
                  <p className="mb-2 text-sm font-medium text-slate-800">信息类型（可多选）</p>
                  <p className="mb-2 text-xs text-slate-500">
                    不勾选任何类型表示<strong>不按类型过滤</strong>。
                  </p>
                  {ruleDraft.catalog_miniprogram_id < 1 ? (
                    <p className="text-sm text-slate-500">请先选择微信小程序。</p>
                  ) : typesLoading ? (
                    <p className="text-sm text-slate-500">加载类型中…</p>
                  ) : catalogTypes.length === 0 ? (
                    <p className="text-sm text-slate-600">该小程序尚未配置信息类型。</p>
                  ) : null}
                  <div className="mt-2 flex flex-wrap gap-x-4 gap-y-2 rounded-lg border border-slate-200 bg-slate-50/80 p-3">
                    <label className="flex cursor-pointer items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        checked={ruleDraft.type_keys.includes('')}
                        onChange={() => toggleRuleTypeKey(UNCAT_KEY)}
                      />
                      <span>未分类（无 type_key）</span>
                    </label>
                    {catalogTypes.map((t) => (
                      <label key={t.key} className="flex cursor-pointer items-center gap-2 text-sm">
                        <input
                          type="checkbox"
                          checked={ruleDraft.type_keys.includes(t.key)}
                          onChange={() => toggleRuleTypeKey(t.key)}
                        />
                        <span>
                          <span className="font-mono text-xs">{t.key}</span>
                          {t.label ? <span className="text-slate-600"> · {t.label}</span> : null}
                        </span>
                      </label>
                    ))}
                  </div>
                </div>
              </Card>
            </div>
          </Modal>

          <Modal
            open={previewModalOpen}
            title="预览汇总（可复制）"
            wide="xl"
            onClose={() => {
              setPreviewModalOpen(false);
              setCopyTip('');
            }}
            footer={
              <div className="flex flex-wrap items-center gap-3">
                <Button type="button" variant="primary" onClick={() => void copyPreviewToClipboard()}>
                  复制正文
                </Button>
                {copyTip ? <span className="text-sm text-slate-600">{copyTip}</span> : null}
                <Button type="button" variant="secondary" onClick={() => setPreviewModalOpen(false)}>
                  关闭
                </Button>
              </div>
            }
          >
            <p className="mb-2 whitespace-pre-wrap text-xs text-slate-600">{previewHead}</p>
            <p className="mb-2 text-xs text-slate-500">
              当前格式：
              {previewBodyFormat === 'rich_html' ? ' HTML 分块（复制为去掉标签后的纯文本，便于粘贴）' : ' 纯文本 + 自定义分隔行'}
              {previewBodyFormat === 'plain_separator' && /<\s*(?:p|img|div|section|br)\b/i.test(previewClipboard) ? (
                <span className="mt-1 block text-slate-600">
                  下方含 HTML 片段；点「复制正文」会先尝试把本站图传到所选公众号素材（需已选公众号且 token 有效），再写入剪贴板；粘贴到公众平台正文时图片为微信域名。请保证{' '}
                  <code className="rounded bg-slate-100 px-1">config.php</code> 的 <code className="rounded bg-slate-100 px-1">site_url</code>{' '}
                  与浏览器里图片地址的域名一致。
                </span>
              ) : null}
            </p>
            {previewBodyFormat === 'plain_separator' && /<\s*(?:p|img|div|section|br)\b/i.test(previewClipboard) ? (
              <div className="mb-3 max-h-[min(36vh,280px)] overflow-auto rounded-lg border border-slate-200 bg-white p-3 text-[15px] leading-relaxed text-slate-900 [&_img]:max-h-[220px] [&_img]:w-auto [&_img]:max-w-full">
                <p className="mb-2 text-xs font-medium text-slate-500">渲染预览</p>
                {/* 预览内容来自本站预览接口，仅管理员可见 */}
                <div dangerouslySetInnerHTML={{ __html: previewClipboard }} />
              </div>
            ) : null}
            <pre className="max-h-[min(58vh,520px)] overflow-auto whitespace-pre-wrap break-words rounded-lg border border-slate-200 bg-slate-50 p-3 text-xs leading-relaxed text-slate-900">
              {previewClipboard}
            </pre>
          </Modal>
        </>
      )}
    </div>
  );
}
